import { __ } from '@wordpress/i18n';
import {
  PanelBody,
  SelectControl,
  RangeControl,
  FormTokenField,
} from '@wordpress/components';
import { useSelect } from '@wordpress/data';
import { store as coreStore } from '@wordpress/core-data';
import { useMemo } from '@wordpress/element';

const POST_TYPE_OPTIONS_BASE = [
  { label: __('Posts', 'brandy-blocks'), value: 'post' },
  { label: __('Pages', 'brandy-blocks'), value: 'page' },
];

const ORDER_OPTIONS = [
  { label: __('Newest to oldest', 'brandy-blocks'), value: 'date/desc' },
  { label: __('Oldest to newest', 'brandy-blocks'), value: 'date/asc' },
  { label: __('A → Z', 'brandy-blocks'), value: 'title/asc' },
  { label: __('Z → A', 'brandy-blocks'), value: 'title/desc' },
  { label: __('Random', 'brandy-blocks'), value: 'rand/desc' },
  { label: __('Menu order', 'brandy-blocks'), value: 'menu_order/asc' },
];

export default function QueryPanel({ attributes, setAttributes }) {
  const query = attributes.query ?? {};
  const presetKey = attributes.presetKey ?? '';

  const updateQuery = (next) =>
    setAttributes({ query: { ...query, ...next } });

  // Presets are localized server-side via PackagesLoader → window.brandyBlocks.
  const presets =
    (typeof window !== 'undefined' && window.brandyBlocks?.mediaGalleryPresets) || [];

  const presetOptions = useMemo(
    () => [
      { label: __('— None (manual query) —', 'brandy-blocks'), value: '' },
      ...presets.map((p) => ({ label: p.label, value: p.key })),
    ],
    [presets]
  );

  // Add Products option only when WooCommerce is active (post type registered).
  const hasWooCommerce = useSelect((select) => {
    const types = select(coreStore).getPostTypes({ per_page: -1 });
    return Array.isArray(types) && types.some((t) => t.slug === 'product');
  }, []);

  const postTypeOptions = useMemo(() => {
    const options = [...POST_TYPE_OPTIONS_BASE];
    if (hasWooCommerce) {
      options.push({ label: __('Products', 'brandy-blocks'), value: 'product' });
    }
    return options;
  }, [hasWooCommerce]);

  const { categories, tags } = useSelect((select) => {
    const { getEntityRecords } = select(coreStore);
    return {
      categories: getEntityRecords('taxonomy', 'category', { per_page: 100 }) ?? [],
      tags: getEntityRecords('taxonomy', 'post_tag', { per_page: 100 }) ?? [],
    };
  }, []);

  const postType = query.postType ?? 'post';
  const showCategoryFilter = postType === 'post';
  const showTagFilter = postType === 'post';

  const selectedCategoryNames = (query.categories ?? [])
    .map((id) => categories.find((c) => c.id === id)?.name)
    .filter(Boolean);
  const selectedTagNames = (query.tags ?? [])
    .map((id) => tags.find((t) => t.id === id)?.name)
    .filter(Boolean);

  const usingPreset = presetKey !== '';

  return (
    <PanelBody title={__('Query', 'brandy-blocks')} initialOpen={true}>
      {presets.length > 0 && (
        <SelectControl
          label={__('Preset', 'brandy-blocks')}
          help={
            usingPreset
              ? __('Preset is active. Manual query controls are hidden.', 'brandy-blocks')
              : __('Pick a preset, or configure the query manually below.', 'brandy-blocks')
          }
          value={presetKey}
          options={presetOptions}
          onChange={(value) => setAttributes({ presetKey: value })}
        />
      )}

      {!usingPreset && (
        <>
          <SelectControl
            label={__('Post type', 'brandy-blocks')}
            value={postType}
            options={postTypeOptions}
            onChange={(value) =>
              updateQuery({ postType: value, categories: [], tags: [] })
            }
          />

          <RangeControl
            label={__('Number of items', 'brandy-blocks')}
            value={query.perPage ?? 6}
            onChange={(value) => updateQuery({ perPage: value })}
            min={1}
            max={24}
          />

          <SelectControl
            label={__('Order by', 'brandy-blocks')}
            value={`${query.orderBy ?? 'date'}/${query.order ?? 'desc'}`}
            options={ORDER_OPTIONS}
            onChange={(value) => {
              const [orderBy, order] = value.split('/');
              updateQuery({ orderBy, order });
            }}
          />

          <RangeControl
            label={__('Offset', 'brandy-blocks')}
            value={query.offset ?? 0}
            onChange={(value) => updateQuery({ offset: value })}
            min={0}
            max={20}
          />

          {showCategoryFilter && categories.length > 0 && (
            <FormTokenField
              label={__('Categories', 'brandy-blocks')}
              value={selectedCategoryNames}
              suggestions={categories.map((c) => c.name)}
              onChange={(tokens) => {
                const ids = tokens
                  .map((name) => categories.find((c) => c.name === name)?.id)
                  .filter(Boolean);
                updateQuery({ categories: ids });
              }}
              __experimentalExpandOnFocus
            />
          )}

          {showTagFilter && tags.length > 0 && (
            <FormTokenField
              label={__('Tags', 'brandy-blocks')}
              value={selectedTagNames}
              suggestions={tags.map((t) => t.name)}
              onChange={(tokens) => {
                const ids = tokens
                  .map((name) => tags.find((t) => t.name === name)?.id)
                  .filter(Boolean);
                updateQuery({ tags: ids });
              }}
              __experimentalExpandOnFocus
            />
          )}
        </>
      )}
    </PanelBody>
  );
}
